import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X, Building2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ORG_TYPE_LABELS } from "./trackNav";

const inputCls = "w-full bg-[#0A0A0A] border border-white/15 focus:border-white outline-none px-3 py-2.5 text-sm transition-colors";
const labelCls = "font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-1.5";
const EMPTY = { name: "", org_type: "club", city: "", state: "", active_season: "", logo_url: "", status: "active" };

export default function TrackOrganizations() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/track/organizations"); setOrgs(data); }
    catch { toast.error("Failed to load organizations."); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/track/organizations", form); toast.success("Organization created."); setForm(EMPTY); setShowAdd(false); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const toggleStatus = async (o) => {
    try { await api.patch(`/track/organizations/${o.id}`, { status: o.status === "active" ? "inactive" : "active" }); load(); }
    catch { toast.error("Update failed."); }
  };
  const del = async (o) => {
    if (!window.confirm(`Delete ${o.name}? Coaches will be unlinked.`)) return;
    try { await api.delete(`/track/organizations/${o.id}`); toast.success("Deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <div data-testid="track-organizations">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl">Organizations</h1>
          <p className="text-white/40 text-sm mt-1" data-testid="track-org-count">{orgs.length} total</p>
        </div>
        <button data-testid="track-add-org" onClick={() => setShowAdd((v) => !v)} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white/85 transition-colors">
          {showAdd ? <X size={14} /> : <Plus size={14} />} {showAdd ? "Cancel" : "New Organization"}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={create} className="bg-[#121212] border border-white/10 p-6 mb-8 grid sm:grid-cols-3 gap-4" data-testid="track-org-form">
          <div><label className={labelCls}>Name *</label><input data-testid="org-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Type</label>
            <select data-testid="org-type" value={form.org_type} onChange={(e) => setForm({ ...form, org_type: e.target.value })} className={inputCls}>
              {Object.entries(ORG_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Active Season</label><input value={form.active_season} onChange={(e) => setForm({ ...form, active_season: e.target.value })} placeholder="2026 Outdoor" className={inputCls} /></div>
          <div><label className={labelCls}>City</label><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>State</label><input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Logo URL</label><input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className={inputCls} /></div>
          <div className="sm:col-span-3"><button data-testid="org-submit" disabled={saving} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors disabled:opacity-60">{saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create</button></div>
        </form>
      )}

      {loading ? <div className="flex justify-center py-24 text-white/40"><Loader2 className="animate-spin" /></div>
        : orgs.length === 0 ? <div className="text-white/40 text-sm border border-white/10 bg-[#121212] p-6" data-testid="track-no-orgs">No organizations yet. Create your first one above.</div>
        : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="track-org-list">
            {orgs.map((o) => (
              <div key={o.id} data-testid={`track-org-${o.id}`} className="bg-[#121212] border border-white/10 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded bg-[#1a1a1a] border border-white/10 flex items-center justify-center overflow-hidden shrink-0">{o.logo_url ? <img src={o.logo_url} alt={o.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <Building2 size={18} className="text-white/30" />}</div>
                  <div className="min-w-0"><p className="font-display font-bold uppercase tracking-tight truncate">{o.name}</p><p className="text-white/40 text-[10px] uppercase tracking-widest">{ORG_TYPE_LABELS[o.org_type] || o.org_type}</p></div>
                </div>
                <div className="text-white/50 text-xs space-y-0.5 mb-4">
                  <p>{[o.city, o.state].filter(Boolean).join(", ") || "—"}</p>
                  <p>Season: {o.active_season || "—"}</p>
                </div>
                <div className="flex items-center justify-between">
                  <button data-testid={`org-status-${o.id}`} onClick={() => toggleStatus(o)} className={`font-mono-accent text-[10px] tracking-[0.15em] uppercase border px-2.5 py-1 transition-colors ${o.status === "active" ? "bg-white text-black border-white" : "text-white/50 border-white/25"}`}>{o.status}</button>
                  <button data-testid={`org-delete-${o.id}`} onClick={() => del(o)} className="text-white/25 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
